import { apiBase, fetchUsingApi } from "@/api/rest/core/apiClient";
import { resumableUpload, StorageUploadError } from "@/api/rest/core/resumableUpload";
import { ApiResponse, getErrorMessage, getNetworkErrorMessage } from "@/api/rest/core/types";
import { sendToSentry } from "@/utils/sentry";

// Files go from the browser straight to storage, not through the API (proxy size limits, timeouts, no progress).
// Endpoints that take files then get the upload ids: see DirectUploadService on the backend.

export interface UploadedFile {
  uploadId: string;
  fileName: string;
}

interface StartedUpload {
  uploadId: string;
  uploadUrl: string;
}

export const getUploadFileName = (file: File): string => (file as File & { path?: string }).path ?? file.name;

const warnBeforeLeaving = (): (() => void) => {
  if (typeof window === "undefined") {
    return () => {};
  }
  const warn = (event: BeforeUnloadEvent) => event.preventDefault();
  window.addEventListener("beforeunload", warn);
  return () => window.removeEventListener("beforeunload", warn);
};

/** Uploads [files] one by one; [onProgress] gets the fraction of all bytes sent, 0..1. */
export const uploadFiles = async (
  files: File[],
  onProgress?: (fraction: number) => void,
): Promise<ApiResponse<UploadedFile[]>> => {
  const totalBytes = Math.max(
    files.reduce((sum, file) => sum + file.size, 0),
    1,
  );
  let doneBytes = 0;
  const uploaded: UploadedFile[] = [];
  const stopWarning = warnBeforeLeaving();
  try {
    for (const file of files) {
      const fileName = getUploadFileName(file);
      const started = await fetchUsingApi(
        `${apiBase}/uploads/v1`,
        "POST",
        (e) => e as StartedUpload,
        JSON.stringify({ fileName, size: file.size }),
      );
      if (!started.success) {
        return started;
      }
      await resumableUpload(started.result.uploadUrl, file, (loaded) =>
        onProgress?.(Math.min((doneBytes + loaded) / totalBytes, 1)),
      );
      doneBytes += file.size;
      uploaded.push({ uploadId: started.result.uploadId, fileName });
    }
    return { success: true, result: uploaded };
  } catch (e) {
    const status = e instanceof StorageUploadError ? e.status : 0;
    sendToSentry(e instanceof Error ? e : new Error(String(e)), {
      additionalContext: { component: "uploadFiles", status, filesCount: files.length, totalBytes },
    });
    return {
      success: false,
      message: status === 0 ? getNetworkErrorMessage() : getErrorMessage(status),
      result: undefined,
    };
  } finally {
    stopWarning();
  }
};

/** Form fields that hand finished uploads to an endpoint that takes files. */
export const appendUploadsToFormData = (formData: FormData, uploads: UploadedFile[]) => {
  uploads.forEach(({ uploadId, fileName }) => {
    formData.append("uploadIds", uploadId);
    formData.append("fileNames", encodeURIComponent(fileName));
  });
};
